/**
 * Remember last valid sales point per tenant (sessionStorage) so intent-led CTAs can
 * reopen slug route first and only fall back to legacy salesPointId query.
 *
 * Also tracks the last-written **commerce** tenant for platform chrome cart badge
 * (FIX 4 F1): browse/shop writes never key carts as `__platform__`, so platform
 * shell needs a session pointer to the real tenant whose lastKnown SP to read.
 */

import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';

const STORAGE_PREFIX = 'customer:lastKnownSalesPoint:';
const LEGACY_KIOSK_PREFIX = 'customer:lastKnownKiosk:';
const LEGACY_PM_PREFIX = 'customer:lastKnownPm:';
/** Session pointer — last tenant that received a rememberSalesPoint* write. */
const LAST_CART_TENANT_KEY = 'customer:lastKnownCartTenant';

/** Same-tab signal when lastKnown SP / cart tenant pointer changes (badge refresh). */
export const CUSTOMER_LAST_KNOWN_SALES_POINT_CHANGED_EVENT =
  'customer:last-known-sales-point-changed' as const;

export interface CustomerLastKnownSalesPointChangedDetail {
  readonly tenantCode: string;
}

function emitLastKnownSalesPointChanged(tenantCode: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<CustomerLastKnownSalesPointChangedDetail>(
      CUSTOMER_LAST_KNOWN_SALES_POINT_CHANGED_EVENT,
      { detail: { tenantCode } },
    ),
  );
}

interface LastKnownSalesPointRecord {
  salesPointId: number;
  salesPointSlug: string | null;
}

/** Legacy persisted shapes — migrated on read. */
interface LegacyLastKnownKioskRecord {
  kioskId: number;
  pmSlug?: string | null;
  kioskSlug?: string | null;
}

function keyForTenant(tenantCode: string): string {
  return `${STORAGE_PREFIX}${tenantCode}`;
}

function legacyKioskKeyForTenant(tenantCode: string): string {
  return `${LEGACY_KIOSK_PREFIX}${tenantCode}`;
}

function legacyPmKeyForTenant(tenantCode: string): string {
  return `${LEGACY_PM_PREFIX}${tenantCode}`;
}

function isRememberableCartTenant(tenantCode: string): boolean {
  const trimmed = tenantCode.trim();
  return trimmed.length > 0 && !isPlatformChromeTenant(trimmed);
}

/**
 * Persist last commerce tenant for platform-chrome badge / cart open.
 * Never stores `__platform__`.
 */
export function rememberLastKnownCartTenant(tenantCode: string): void {
  if (typeof window === 'undefined' || !isRememberableCartTenant(tenantCode)) {
    return;
  }
  try {
    window.sessionStorage.setItem(LAST_CART_TENANT_KEY, tenantCode.trim());
  } catch {
    /* ignore quota / private mode */
  }
}

/** Last commerce tenant written via rememberSalesPoint* — or null. */
export function readLastKnownCartTenant(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(LAST_CART_TENANT_KEY);
    if (raw === null) {
      return null;
    }
    const trimmed = raw.trim();
    if (!isRememberableCartTenant(trimmed)) {
      return null;
    }
    return trimmed;
  } catch {
    return null;
  }
}

function readRawStorage(tenantCode: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const current = window.sessionStorage.getItem(keyForTenant(tenantCode));
  if (current !== null) {
    return current;
  }
  const legacyKiosk = window.sessionStorage.getItem(legacyKioskKeyForTenant(tenantCode));
  if (legacyKiosk !== null) {
    try {
      window.sessionStorage.setItem(keyForTenant(tenantCode), legacyKiosk);
      window.sessionStorage.removeItem(legacyKioskKeyForTenant(tenantCode));
    } catch {
      /* ignore quota / private mode */
    }
    return legacyKiosk;
  }
  const legacyPm = window.sessionStorage.getItem(legacyPmKeyForTenant(tenantCode));
  if (legacyPm === null) {
    return null;
  }
  try {
    window.sessionStorage.setItem(keyForTenant(tenantCode), legacyPm);
    window.sessionStorage.removeItem(legacyPmKeyForTenant(tenantCode));
  } catch {
    /* ignore quota / private mode */
  }
  return legacyPm;
}

/** Sales-point slug shape (N13 / platform return targets) — export for reuse. */
export const SALES_POINT_SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function normalizeSalesPointSlug(slug: string | null | undefined): string | null {
  if (typeof slug !== 'string') {
    return null;
  }
  const trimmed = slug.trim();
  return SALES_POINT_SLUG_REGEX.test(trimmed) ? trimmed : null;
}

function parseRecord(raw: string): LastKnownSalesPointRecord | null {
  if (/^\d+$/.test(raw)) {
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n) || n <= 0) {
      return null;
    }
    return { salesPointId: n, salesPointSlug: null };
  }
  const parsed = JSON.parse(raw) as LegacyLastKnownKioskRecord & {
    salesPointId?: number;
    salesPointSlug?: string | null;
  };
  if (parsed === null || typeof parsed !== 'object') {
    return null;
  }
  let id: number | null = null;
  if (Number.isFinite(parsed.salesPointId) && (parsed.salesPointId ?? 0) > 0) {
    id = Math.floor(parsed.salesPointId as number);
  } else if (Number.isFinite(parsed.kioskId) && parsed.kioskId > 0) {
    id = Math.floor(parsed.kioskId);
  }
  if (id === null) {
    return null;
  }
  const salesPointSlug =
    normalizeSalesPointSlug(parsed.salesPointSlug) ??
    normalizeSalesPointSlug(parsed.kioskSlug) ??
    normalizeSalesPointSlug(parsed.pmSlug);
  return { salesPointId: id, salesPointSlug };
}

export function rememberSalesPointIdForTenant(tenantCode: string, salesPointId: number): void {
  if (
    typeof window === 'undefined' ||
    typeof tenantCode !== 'string' ||
    tenantCode.length === 0 ||
    !Number.isFinite(salesPointId) ||
    salesPointId <= 0
  ) {
    return;
  }
  try {
    const payload: LastKnownSalesPointRecord = {
      salesPointId: Math.floor(salesPointId),
      salesPointSlug: null,
    };
    window.sessionStorage.setItem(keyForTenant(tenantCode), JSON.stringify(payload));
    rememberLastKnownCartTenant(tenantCode);
    emitLastKnownSalesPointChanged(tenantCode);
  } catch {
    /* ignore quota / private mode */
  }
}

export function rememberSalesPointForTenant(
  tenantCode: string,
  salesPointId: number,
  salesPointSlug: string | null
): void {
  if (
    typeof window === 'undefined' ||
    typeof tenantCode !== 'string' ||
    tenantCode.length === 0 ||
    !Number.isFinite(salesPointId) ||
    salesPointId <= 0
  ) {
    return;
  }
  try {
    const payload: LastKnownSalesPointRecord = {
      salesPointId: Math.floor(salesPointId),
      salesPointSlug: normalizeSalesPointSlug(salesPointSlug),
    };
    window.sessionStorage.setItem(keyForTenant(tenantCode), JSON.stringify(payload));
    rememberLastKnownCartTenant(tenantCode);
    emitLastKnownSalesPointChanged(tenantCode);
  } catch {
    /* ignore quota / private mode */
  }
}

export function readLastKnownSalesPointId(tenantCode: string): number | null {
  if (typeof window === 'undefined' || typeof tenantCode !== 'string' || tenantCode.length === 0) {
    return null;
  }
  try {
    const raw = readRawStorage(tenantCode);
    if (raw === null || raw.length === 0) {
      return null;
    }
    const parsed = parseRecord(raw);
    return parsed?.salesPointId ?? null;
  } catch {
    return null;
  }
}

export function readLastKnownSalesPointSlug(tenantCode: string): string | null {
  if (typeof window === 'undefined' || typeof tenantCode !== 'string' || tenantCode.length === 0) {
    return null;
  }
  try {
    const raw = readRawStorage(tenantCode);
    if (raw === null || raw.length === 0) {
      return null;
    }
    const parsed = parseRecord(raw);
    return parsed?.salesPointSlug ?? null;
  } catch {
    return null;
  }
}

export function readLastKnownSalesPoint(
  tenantCode: string
): { salesPointId: number; salesPointSlug: string | null } | null {
  const salesPointId = readLastKnownSalesPointId(tenantCode);
  if (salesPointId === null) {
    return null;
  }
  return { salesPointId, salesPointSlug: readLastKnownSalesPointSlug(tenantCode) };
}

/** @deprecated Use rememberSalesPointIdForTenant */
export const rememberKioskIdForTenant = rememberSalesPointIdForTenant;

/** @deprecated Use rememberSalesPointForTenant */
export const rememberKioskForTenant = rememberSalesPointForTenant;

/** @deprecated Use readLastKnownSalesPointId */
export const readLastKnownKioskId = readLastKnownSalesPointId;

/** @deprecated Use readLastKnownSalesPointSlug */
export const readLastKnownKioskSlug = readLastKnownSalesPointSlug;

/** @deprecated Use readLastKnownSalesPoint */
export const readLastKnownKiosk = readLastKnownSalesPoint;
